import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  INTENTIONS_IA,
  LIBELLES_INTENTION_IA,
  LIBELLES_OBJECTIF,
  LIBELLES_TON,
  OBJECTIFS_CAMPAGNE,
  TONS_MESSAGE,
  type IntentionIA,
  type ObjectifCampagne,
  type ReponseAssistant,
  type Segment,
  type TonMessage,
} from '@iobiz/shared'
import { ApiError, api } from '../../api/client.js'

/**
 * L'ASSISTANT IA MARKETING, en colonne à côté du formulaire.
 *
 * ⭐ À CÔTÉ, pas dans une fenêtre par-dessus : on écrit son message ET on
 * demande conseil, sans perdre de vue ce qu'on est en train de rédiger. Une
 * boîte modale par-dessus une autre aurait obligé à refermer pour comparer.
 *
 * ⭐ Chaque proposition se pose dans le message d'un clic. Un conseiller dont
 * il faut recopier la réponse à la main n'est pas un conseiller, c'est un
 * article de blog.
 */
export function AssistantIA({
  storeId,
  message,
  segment,
  onUtiliser,
}: {
  storeId: string
  /** Le message en cours de rédaction — l'assistant travaille dessus. */
  message: string
  segment: Segment
  /** Pose le texte dans le champ « message » du formulaire. */
  onUtiliser: (texte: string) => void
}): JSX.Element {
  const [intention, setIntention] = useState<IntentionIA>('redaction')
  const [objectif, setObjectif] = useState<ObjectifCampagne>('promotion')
  const [ton, setTon] = useState<TonMessage>('chaleureux')
  const [precisions, setPrecisions] = useState('')
  const [reponse, setReponse] = useState<ReponseAssistant | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  /** Ces intentions travaillent sur un message existant : sans lui, rien à dire. */
  const exigeMessage = intention === 'ton' || intention === 'optimisation' || intention === 'controle'
  const bloque = exigeMessage && message.trim().length < 10

  async function demander(): Promise<void> {
    setOccupe(true)
    setErreur(null)
    try {
      const r = await api.post<ReponseAssistant>(`/stores/${storeId}/campaigns/assistant`, {
        intention,
        objectif,
        ton,
        message: message.trim() || undefined,
        precisions: precisions.trim() || undefined,
        segment,
      })
      setReponse(r)
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'L’assistant n’a pas répondu.')
    } finally {
      setOccupe(false)
    }
  }

  /** Une réponse rédigée se pose dans le message ; un conseil, non. */
  const redactionnelle =
    intention === 'redaction' || intention === 'ton' || intention === 'optimisation'

  return (
    <Paper
      variant="outlined"
      sx={{ p: 4, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Assistant marketing
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {INTENTIONS_IA.map((i) => (
          <Chip
            key={i}
            label={LIBELLES_INTENTION_IA[i]}
            size="small"
            variant={intention === i ? 'filled' : 'outlined'}
            color={intention === i ? 'primary' : 'default'}
            onClick={() => {
              setIntention(i)
              setReponse(null)
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 3 }}>
        <TextField
          select
          size="small"
          label="Objectif"
          value={objectif}
          onChange={(e) => setObjectif(e.target.value as ObjectifCampagne)}
          sx={{ flex: 1 }}
        >
          {OBJECTIFS_CAMPAGNE.map((o) => (
            <MenuItem key={o} value={o}>
              {LIBELLES_OBJECTIF[o]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Ton"
          value={ton}
          onChange={(e) => setTon(e.target.value as TonMessage)}
          sx={{ minWidth: 140 }}
        >
          {TONS_MESSAGE.map((t) => (
            <MenuItem key={t} value={t}>
              {LIBELLES_TON[t]}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TextField
        size="small"
        label="Précisions"
        placeholder="-20 % sur les massages jusqu’à samedi"
        value={precisions}
        onChange={(e) => setPrecisions(e.target.value)}
        multiline
        minRows={2}
        helperText="L’assistant n’invente ni remise ni date : ce que vous écrivez ici est la seule information chiffrée qu’il utilisera."
      />

      <Tooltip title={bloque ? 'Écrivez d’abord un message à travailler.' : ''}>
        <span>
          <Button
            fullWidth
            variant="contained"
            disabled={occupe || bloque}
            startIcon={occupe ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={() => void demander()}
          >
            {occupe ? 'Réflexion…' : 'Demander conseil'}
          </Button>
        </span>
      </Tooltip>

      {erreur && <Alert severity="error">{erreur}</Alert>}

      {reponse && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* ⭐ La provenance est AFFICHÉE. Un gérant a le droit de savoir s'il
              lit une proposition écrite pour son salon ou une phrase de
              gabarit — et de la juger en conséquence. */}
          <Chip
            size="small"
            variant="outlined"
            color={reponse.source === 'ia' ? 'primary' : 'default'}
            label={
              reponse.source === 'ia'
                ? 'Rédigé par l’assistant'
                : 'Conseils intégrés (assistant IA non configuré)'
            }
            sx={{ alignSelf: 'flex-start' }}
          />

          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {reponse.texte}
            </Typography>
          </Paper>

          {redactionnelle && (
            <Button
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => onUtiliser(reponse.texte)}
            >
              Utiliser ce texte
            </Button>
          )}

          {reponse.suggestions.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {redactionnelle ? 'Variantes' : 'Propositions'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                {reponse.suggestions.map((s, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    role={redactionnelle ? 'button' : undefined}
                    tabIndex={redactionnelle ? 0 : undefined}
                    onClick={redactionnelle ? () => onUtiliser(s) : undefined}
                    onKeyDown={(e) => {
                      if (redactionnelle && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        onUtiliser(s)
                      }
                    }}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      cursor: redactionnelle ? 'pointer' : 'default',
                      '&:hover': redactionnelle ? { borderColor: 'primary.main' } : {},
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {s}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  )
}
